if (!customElements.get('shop-the-look-modal')) {
  customElements.define(
    'shop-the-look-modal',
    class ShopTheLookModal extends ModalDialog {
      constructor() {
        super();
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        const event = new CustomEvent('modal-dialog:opened', {
          bubbles: true,
          detail: { elementToFocus: this },
        });
        document.dispatchEvent(event);
      }
    }
  );
}

if (!customElements.get('ways-to-wear')) {
  customElements.define(
    'ways-to-wear',
    class WaysToWear extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        // Each panel has a scrollable product grid (desktop) with its own progress bar.
        this.scrollers = this.panels.map((panel) => {
          const scroller = panel.querySelector('.ways-to-wear__products');
          const bar = panel.querySelector('.ways-to-wear__scrollbar');
          const progress = bar?.querySelector('.slider-scrollbar__progress');
          if (scroller && bar && progress) {
            scroller.addEventListener('scroll', () => this.updateBar(scroller, bar, progress), {
              passive: true,
            });
          }
          return { scroller, bar, progress };
        });

        // The section's styles load via an async <link>, so the scroller isn't height-
        // constrained when this first runs. A ResizeObserver re-measures once the CSS
        // (and any font/content reflow) settles, and also covers breakpoint changes.
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.updateAllBars());
          this.scrollers.forEach(({ scroller }) => {
            if (scroller) this.resizeObserver.observe(scroller);
          });
        }

        this.mediaQuery = window.matchMedia('(min-width: 750px)');
        this.handleViewportChange = () => {
          this.applyMode();
          this.updateAllBars();
        };
        this.mediaQuery.addEventListener('change', this.handleViewportChange);

        // Recompute progress bars on resize (container height tracks viewport width).
        let resizeRaf = null;
        this.handleResize = () => {
          if (resizeRaf) cancelAnimationFrame(resizeRaf);
          resizeRaf = requestAnimationFrame(() => this.updateAllBars());
        };
        window.addEventListener('resize', this.handleResize, { passive: true });

        if (this.tabs.length >= 2) {
          this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => this.activate(index));
            tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
          });

          // Tabs only apply on desktop. On mobile, every look is stacked and all
          // panels stay visible, so we mirror that in the DOM (clearing the [hidden]
          // attribute and aria state) so assistive tech sees them.
          this.applyMode();
        }

        this.updateAllBars();
      }

      disconnectedCallback() {
        this.mediaQuery?.removeEventListener('change', this.handleViewportChange);
        if (this.handleResize) window.removeEventListener('resize', this.handleResize);
        this.resizeObserver?.disconnect();
      }

      updateBar(scroller, bar, progress) {
        if (!scroller || !bar || !progress) return;
        const { scrollHeight, clientHeight, scrollTop } = scroller;
        const scrollable = scrollHeight - clientHeight > 1;
        bar.classList.toggle('is-scrollable', scrollable);
        if (!scrollable) return;
        // Fill tracks how much of the content has been revealed: it starts at the
        // visible fraction (e.g. ~2/3 when 2 of 3 rows show) and reaches 100% at the
        // bottom. Equivalent to the position of the viewport's bottom edge.
        progress.style.setProperty('--scrollbar-percent', `${((scrollTop + clientHeight) / scrollHeight) * 100}%`);
      }

      updateAllBars() {
        // Hidden panels report a height of 0, so only visible panels yield a useful
        // measurement — that's fine, the active panel updates again when shown.
        this.scrollers?.forEach(({ scroller, bar, progress }) => this.updateBar(scroller, bar, progress));
      }

      applyMode() {
        if (this.mediaQuery.matches) {
          // Desktop: a single panel is visible, matching the currently selected tab.
          const activeIndex = Math.max(
            0,
            this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
          );
          this.tabs.forEach((tab, i) => {
            const isActive = i === activeIndex;
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            tab.tabIndex = isActive ? 0 : -1;
          });
          this.panels.forEach((panel, i) => {
            panel.toggleAttribute('hidden', i !== activeIndex);
          });
        } else {
          // Mobile: stack all looks; tabs are hidden in CSS but their state is
          // preserved so resizing back to desktop picks up where the user left off.
          this.panels.forEach((panel) => panel.removeAttribute('hidden'));
        }
      }

      activate(index) {
        this.tabs.forEach((tab, i) => {
          const isActive = i === index;
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.tabIndex = isActive ? 0 : -1;
        });
        // Tabs are only interactive on desktop; on mobile all panels stay visible.
        if (this.mediaQuery?.matches !== false) {
          this.panels.forEach((panel, i) => {
            panel.toggleAttribute('hidden', i !== index);
          });
          // The newly shown panel now has layout, so recompute its scrollbar.
          const shown = this.scrollers?.[index];
          if (shown) this.updateBar(shown.scroller, shown.bar, shown.progress);
        }
        this.tabs[index].focus();
      }

      onKeydown(event, index) {
        const key = event.key;
        let nextIndex;

        if (key === 'ArrowRight') {
          nextIndex = (index + 1) % this.tabs.length;
        } else if (key === 'ArrowLeft') {
          nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        } else if (key === 'Home') {
          nextIndex = 0;
        } else if (key === 'End') {
          nextIndex = this.tabs.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        this.activate(nextIndex);
      }
    }
  );
}
